import { DEFAULT_LIMIT, DEFAULT_SKIP, LIMIT } from 'Src/consts/table';
import { Count } from 'Src/models/count';
import { superAdminGet, superAdminPut } from './api';

export interface SuperAdminPermissionRequest {
  id: string;
  createdAt?: string;
  updatedAt?: string;
  requesterId?: string;
  requester?: {
    id?: string;
    firstName?: string;
    lastName?: string;
    email?: string;
    phone?: string;
    birthday?: string;
    gender?: string;
    [key: string]: any;
  };
  requestedPermission?: {
    preset?: string;
    schoolId?: string;
    school?: {
      id?: string;
      name?: string;
      pic?: string;
      [key: string]: any;
    };
    comment?: string;
    details?: any;
    tournamentId?: string;
    [key: string]: any;
  };
  tournament?: {
    id?: string;
    name?: string;
    [key: string]: any;
  };
  sports?: Array<{
    id?: string;
    name?: string;
  }>;
  sportIds?: string[];
  status?: string;
  promo?: string;
  statusSetAt?: string;
  statusSetBy?: any;
  [key: string]: any;
}

export interface SuperAdminPermissionRequestsFilter {
  limit?: number;
  skip?: number;
  order?: string;
  where?: any;
}

export interface SuperAdminPermissionRequestStatusData {
  status: 'ACCEPTED' | 'REJECTED';
  studentId?: string;
  formId?: string;
  houseId?: string;
  sportIds?: string[];
}

const DEFAULT_STATUS = 'NEW';

const DEFAULT_FILTER: SuperAdminPermissionRequestsFilter = {
  limit: LIMIT,
  skip: DEFAULT_SKIP,
  order: 'createdAt DESC',
  where: {
    status: DEFAULT_STATUS
  }
};

function isEmptyFilterValue(value: any): boolean {
  return typeof value === 'undefined' || value === null || value === '' || (Array.isArray(value) && value.length === 0);
}

function trimTextValue(value: any): any {
  return typeof value === 'string' ? value.trim() : value;
}

function normalizeFilterValue(value: any): any {
  if (isEmptyFilterValue(value)) {
    return undefined;
  }

  if (typeof value === 'string') {
    return value.trim();
  }

  if (Array.isArray(value)) {
    return value.map(item => trimTextValue(item)).filter(item => !isEmptyFilterValue(item));
  }

  if (typeof value === 'object') {
    const result = { ...value };

    if (typeof result.like === 'string') {
      result.like = result.like.trim();
    }

    if (typeof result.$in !== 'undefined' && !Array.isArray(result.$in)) {
      result.$in = [result.$in];
    }

    if (Array.isArray(result.$in)) {
      result.$in = result.$in.map(item => trimTextValue(item)).filter(item => !isEmptyFilterValue(item));
    }

    Object.keys(result).forEach(key => {
      if (isEmptyFilterValue(result[key])) {
        delete result[key];
      }
    });

    return Object.keys(result).length === 0 ? undefined : result;
  }

  return value;
}

function buildWhere(where?: any) {
  const result = {};

  Object.keys(where || {}).forEach(key => {
    const normalizedValue = normalizeFilterValue(where[key]);

    if (!isEmptyFilterValue(normalizedValue)) {
      result[key] = normalizedValue;
    }
  });

  const hasExplicitStatus = !isEmptyFilterValue(result['status']);
  const hasAnyOtherFilter = Object.keys(result).some(key => key !== 'status');

  if (!hasExplicitStatus && !hasAnyOtherFilter) {
    result['status'] = DEFAULT_STATUS;
  }

  return result;
}

function buildFilter(filter: SuperAdminPermissionRequestsFilter = DEFAULT_FILTER): SuperAdminPermissionRequestsFilter {
  return {
    where: buildWhere(filter.where),
    skip: typeof filter.skip === 'number' ? filter.skip : DEFAULT_FILTER.skip,
    limit: typeof filter.limit === 'number' ? filter.limit : DEFAULT_FILTER.limit,
    order: filter.order || DEFAULT_FILTER.order
  };
}

export function getSuperAdminPermissionRequests(
  filter: SuperAdminPermissionRequestsFilter = DEFAULT_FILTER
): Promise<SuperAdminPermissionRequest[]> {
  return superAdminGet<SuperAdminPermissionRequest[]>('/users/permissions/requests', {
    params: {
      filter: buildFilter(filter)
    }
  });
}

export function getAllSuperAdminPermissionRequests(where?: any): Promise<SuperAdminPermissionRequest[]> {
  return superAdminGet<SuperAdminPermissionRequest[]>('/users/permissions/requests', {
    params: {
      filter: {
        where: buildWhere(where),
        skip: DEFAULT_SKIP,
        limit: DEFAULT_LIMIT,
        order: DEFAULT_FILTER.order
      }
    }
  });
}

export function getSuperAdminPermissionRequestsCount(where?: any): Promise<Count> {
  return superAdminGet<Count>('/users/permissions/requests/count', {
    params: {
      filter: {
        where: buildWhere(where)
      }
    }
  });
}

export function getSuperAdminPermissionRequest(requestId: string): Promise<SuperAdminPermissionRequest> {
  return superAdminGet<SuperAdminPermissionRequest>(`/users/permissions/requests/${requestId}`);
}

export function updateSuperAdminPermissionRequestStatus(
  requestId: string,
  data: SuperAdminPermissionRequestStatusData
): Promise<SuperAdminPermissionRequest> {
  return superAdminPut<SuperAdminPermissionRequest>(`/users/permissions/requests/${requestId}/status`, data);
}
